// Stock wherever items show (Mitch's inventory manager, 2026-11-03: "For any place that shows items, We'd want to see all
// shopify inventory counts, available, and the other categorizations. Some can be hidden by default … The incoming count
// should be shown, too"): one set of columns, the same on Sell items, the Parts library and a PO's lines. Available,
// On hand and Incoming show; Committed, what Shopify holds back (damaged, quality control, safety stock, reserved) and
// what's on drafts are in Columns (optional). Counts are HHL's (shared/stock.ts): Shopify's at HHL's locations plus the
// ledger for local items; incoming is what open POs to HHL still have to bring.
import { type HhlStock, hhlStock, type Incoming, incomingByItem, stockNote } from "../../../shared/stock.ts";
import { qty } from "../lib/format.ts";
import { useRows } from "../lib/sync.ts";
import { cx } from "./controls.tsx";
import type { Column } from "./DataTable.tsx";
import { StockNumber } from "./StockEditor.tsx";

export type StockIndex = { atHhl: Map<string, HhlStock>; incoming: Map<string, Incoming> };

/** Worked out once for the synced rows it's from, shared by every table and row that asks (a count screen's lines). */
let cached: { inputs: readonly unknown[]; ix: StockIndex } | null = null;

export function useStockIndex(): StockIndex {
  const inputs = [useRows("stock_level"), useRows("stock_balance"), useRows("location"), useRows("po"), useRows("po_line")] as const;
  const [stock, ledger, locations, pos, lines] = inputs;
  if (!cached || cached.inputs.some((x, i) => x !== inputs[i]))
    cached = { inputs, ix: { atHhl: hhlStock(stock, ledger, locations), incoming: incomingByItem(pos, lines, locations) } };
  return cached.ix;
}

type Key = "available" | "on_hand" | "incoming" | "committed" | "damaged" | "quality_control" | "safety_stock" | "reserved" | "on_drafts";

const HELD: { key: Key; header: string; title: string; of: (s: HhlStock) => number }[] = [
  { key: "committed", header: "Committed", title: "On customer orders that haven't shipped yet (on hand, not available)", of: (s) => s.committed },
  { key: "damaged", header: "Damaged", title: "Shopify's damaged count at HHL (on hand, not available)", of: (s) => s.damaged },
  { key: "quality_control", header: "Quality control", title: "Shopify's quality control count at HHL (on hand, not available)", of: (s) => s.qualityControl },
  { key: "safety_stock", header: "Safety stock", title: "Shopify's safety stock at HHL (on hand, not available)", of: (s) => s.safetyStock },
  { key: "reserved", header: "Reserved", title: "Shopify's reserved count at HHL (on hand, not available)", of: (s) => s.reserved },
];

/**
 * The stock columns for a table of rows that are items (or lines of one: `itemOf`). `skip` leaves out ones the table
 * shows its own way (Sell items' Available, which adds what's at the CMs).
 */
export function stockColumns<T>(ix: StockIndex, itemOf: (row: T) => string | null | undefined, opts: { skip?: Key[] } = {}): Column<T>[] {
  const at = (r: T) => ix.atHhl.get(itemOf(r) ?? "");
  const inc = (r: T) => ix.incoming.get(itemOf(r) ?? "");
  // A count (with `item`): clicking it changes the item's counts (StockEditor), never opening the row.
  const num = (n: number | undefined, title?: string | null, warn?: boolean, item?: string | null) => {
    const look = cx(title && "underline decoration-dotted underline-offset-2", warn && "text-danger", !n && "text-faint");
    return item ? (
      <StockNumber itemId={item} className={look} title={title ?? undefined}>
        {qty(n ?? 0)}
      </StockNumber>
    ) : (
      <span className={look} title={title ?? undefined}>
        {qty(n ?? 0)}
      </span>
    );
  };
  const cols: (Column<T> & { key: Key })[] = [
    {
      key: "available",
      header: "Available",
      headerTitle: "At HHL: on hand less what's committed to customer orders and what Shopify holds back. Hover a number for the split.",
      width: "96px",
      align: "right",
      value: (r) => at(r)?.available ?? null,
      cell: (r) => num(at(r)?.available, stockNote(at(r)), (at(r)?.available ?? 0) < 0, itemOf(r)),
    },
    {
      key: "on_hand",
      header: "On hand",
      headerTitle: "On the shelf at HHL: available, committed and what Shopify holds back (damaged, quality control, safety stock, reserved)",
      width: "88px",
      align: "right",
      value: (r) => at(r)?.onHand ?? null,
      cell: (r) => num(at(r)?.onHand, null, false, itemOf(r)),
    },
    {
      key: "incoming",
      header: "Incoming",
      headerTitle: "Still to arrive at HHL on POs the vendors have (sent, not received). Hover for the POs.",
      width: "88px",
      align: "right",
      value: (r) => inc(r)?.incoming ?? null,
      cell: (r) => {
        const s = inc(r);
        const sent = s?.pos.filter((p) => !p.draft) ?? [];
        return num(s?.incoming, sent.length ? sent.map((p) => `${p.number}: ${qty(p.qty)}`).join(" · ") : null);
      },
    },
    ...HELD.map((h): Column<T> & { key: Key } => ({
      key: h.key,
      header: h.header,
      headerTitle: h.title,
      width: "96px",
      align: "right",
      optional: true,
      value: (r) => {
        const s = at(r);
        return s ? h.of(s) : null;
      },
      cell: (r) => {
        const s = at(r);
        // Committed is customer orders': not a count anyone changes here.
        return num(s ? h.of(s) : 0, null, false, h.key === "committed" ? null : itemOf(r));
      },
    })),
    {
      key: "on_drafts",
      header: "On drafts",
      headerTitle: "On draft POs not sent yet (not incoming until the vendor has them). Hover for the drafts.",
      width: "88px",
      align: "right",
      optional: true,
      value: (r) => inc(r)?.drafted ?? null,
      cell: (r) => {
        const s = inc(r);
        const drafts = s?.pos.filter((p) => p.draft) ?? [];
        return num(s?.drafted, drafts.length ? drafts.map((p) => `${p.number}: ${qty(p.qty)}`).join(" · ") : null);
      },
    },
  ];
  // A group's row (a product's variants) adds them up.
  return cols
    .filter((c) => !opts.skip?.includes(c.key))
    .map((c) => ({ ...c, groupCell: (rs: T[]) => qty(rs.reduce((n, r) => n + Number(c.value?.(r) ?? 0), 0)) }));
}
